"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  NumberInput,
  RememberedTextInput,
  RememberedNamesNote,
  CheckboxRow,
  ResultBanner,
  PrintButton,
  CopyButton,
  DataReviewedNote,
  PrintFooter,
  trackToolResult,
} from "@/components/tools/ui";

/* ── BS 5306-8 Class A portable-extinguisher provision ─────────────────────
   Verified against BS 5306-8 (current edition 2023) summaries and HSE
   construction fire guidance:
   - Class A cover is driven by floor area: minimum aggregate combined Class A
     rating = 0.065 × floor area (m²) per storey.
   - Minimum 2 Class A extinguishers per storey with a combined rating of at
     least 26A. A storey of 50 m² or less may be covered by a single
     extinguisher.
   - A typical 13A extinguisher (e.g. 9 L water or 6 L foam) contributes 13
     toward the aggregate, and covers roughly 200 m² (13 ÷ 0.065).
   - Maximum 30 m travel distance to a Class A extinguisher.
   Sources: BS 5306-8; HSE hse.gov.uk/construction/safetytopics/generalfire.htm;
   HSG168 Fire safety in construction (portable provision, hot works fire point).
   This covers portable extinguisher provision only — not fixed systems, not a
   fire risk assessment. */

const A_FACTOR = 0.065; // combined A rating per m²
const RATING_PER_UNIT = 13; // a typical 13A extinguisher
const MIN_RATING_PER_STOREY = 26; // 2 × 13A
const SINGLE_OK_BELOW = 50; // m² — a very small storey may have one
const TRAVEL_DISTANCE = 30; // m, Class A

interface FloorRow {
  id: number;
  label: string;
  area: string; // m²
}

let nextId = 1;
function emptyRow(label = ""): FloorRow {
  return { id: nextId++, label, area: "" };
}

interface RiskOption {
  key: string;
  label: string;
  /** What to add and why — appears on the schedule. */
  addOn: string;
}

/* Type add-ons keyed to the risk present. Water is never the first choice near
   live electrics; CO₂ for electrical; foam (or AFFF) for flammable liquids;
   wet chemical for cooking oils; hot works carry their own fire point. */
const RISK_OPTIONS: RiskOption[] = [
  {
    key: "electrical",
    label: "Electrical equipment / live distribution (boards, generators, server or plant rooms)",
    addOn:
      "Add a CO₂ extinguisher (commonly 2 kg) at the electrical risk — never water or foam on or near live electrics.",
  },
  {
    key: "flammable-liquids",
    label: "Flammable liquids, paints, solvents or fuels stored or in use",
    addOn:
      "Add a foam (AFFF) extinguisher rated for flammable liquids (a Class B rating) at the storage or decanting point. Keep quantities to a day's use and store the rest in a fire-rated store.",
  },
  {
    key: "hot-works",
    label: "Hot works — welding, grinding, cutting, soldering, bitumen / torch-on",
    addOn:
      "Provide a fire point at each hot-works position (HSG168 / hot-works permit): a suitable extinguisher within reach, a charged fire watch, and 60 minutes of fire watch after work stops.",
  },
  {
    key: "cooking",
    label: "Welfare / canteen with cooking (deep-fat fryer or hob)",
    addOn:
      "Add a wet chemical extinguisher and a fire blanket at the cooking position — water, foam and CO₂ are not suitable for cooking-oil (Class F) fires.",
  },
  {
    key: "lpg-gas",
    label: "Flammable gases / LPG cylinders (heating, gas cutting, bitumen boilers)",
    addOn:
      "Dry powder is the usual provision where flammable gases are present, but the priority is isolating the supply and removing cylinders — extinguishing a gas fire without stopping the leak risks an explosive re-ignition.",
  },
];

interface StoreyResult {
  row: FloorRow;
  area: number;
  requiredRating: number; // 0.065 × area, but at least 26
  unitsByRating: number; // ceil(requiredRating / 13)
  units: number; // applying the per-storey minimum
  minApplied: boolean; // the 2-per-storey (or 1 for tiny) floor kicked in
  singleAllowed: boolean;
}

function storeyResult(row: FloorRow): StoreyResult | null {
  const area = parseFloat(row.area);
  if (!isFinite(area) || area <= 0) return null;
  const ratingFromArea = A_FACTOR * area;
  const singleAllowed = area <= SINGLE_OK_BELOW;
  // Aggregate rating must meet 0.065 × area and the per-storey minimum (26A,
  // unless a very small storey where one extinguisher is acceptable).
  const floorRating = singleAllowed ? RATING_PER_UNIT : MIN_RATING_PER_STOREY;
  const requiredRating = Math.max(ratingFromArea, floorRating);
  const unitsByRating = Math.ceil((A_FACTOR * area) / RATING_PER_UNIT);
  const floorUnits = singleAllowed ? 1 : 2;
  const units = Math.max(unitsByRating, floorUnits);
  const minApplied = units > unitsByRating || (unitsByRating < floorUnits);
  return { row, area, requiredRating, unitsByRating, units, minApplied, singleAllowed };
}

export function FireExtinguisherCalculator() {
  const [rows, setRows] = useState<FloorRow[]>([emptyRow("Ground floor")]);
  const [site, setSite] = useState("");
  const [risks, setRisks] = useState<Record<string, boolean>>({});
  const resultTracked = useRef(false);

  const update = (id: number, patch: Partial<FloorRow>) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const toggleRisk = (key: string, v: boolean) => setRisks((r) => ({ ...r, [key]: v }));

  const computed = useMemo(() => {
    const results = rows
      .map(storeyResult)
      .filter((r): r is StoreyResult => r !== null);
    if (results.length === 0) return null;
    const totalUnits = results.reduce((s, r) => s + r.units, 0);
    const totalArea = results.reduce((s, r) => s + r.area, 0);
    const activeRisks = RISK_OPTIONS.filter((o) => risks[o.key]);
    return { results, totalUnits, totalArea, activeRisks };
  }, [rows, risks]);

  useEffect(() => {
    if (computed && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("fire-extinguisher-calculator", {
        storeys: computed.results.length,
        units: computed.totalUnits,
      });
    }
  }, [computed]);

  const buildCopyText = () => {
    if (!computed) return "";
    const lines: string[] = [];
    lines.push("PORTABLE FIRE EXTINGUISHER PROVISION SCHEDULE");
    if (site) lines.push(`Site: ${site}`);
    lines.push(`Date: ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`);
    lines.push("Basis: BS 5306-8 Class A cover (0.065 x floor area, min 26A / 2 per storey).");
    lines.push("");
    computed.results.forEach((r) => {
      lines.push(
        `${r.row.label || "Storey"} — ${r.area} m²: ${r.units} x 13A extinguisher${r.units === 1 ? "" : "s"} ` +
          `(combined ${r.units * RATING_PER_UNIT}A; required ${Math.ceil(r.requiredRating)}A).`,
      );
    });
    lines.push("");
    lines.push(`Total Class A extinguishers (13A-equivalent): ${computed.totalUnits}`);
    lines.push(`Maximum travel distance to a Class A extinguisher: ${TRAVEL_DISTANCE} m.`);
    if (computed.activeRisks.length > 0) {
      lines.push("");
      lines.push("Risk-specific add-ons:");
      computed.activeRisks.forEach((o) => lines.push(`- ${o.addOn}`));
    }
    lines.push("");
    lines.push(
      "Covers portable extinguisher provision only — not fixed systems and not a fire risk assessment. " +
        "Confirm against a competent person and your site fire risk assessment.",
    );
    return lines.join("\n");
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* Inputs */}
      <div className="space-y-6">
        <div className="card shadow-soft p-6 print-hide">
          <h2 className="text-xl">Floors to cover</h2>
          <p className="mt-1 text-sm text-muted">
            Enter the floor area of each storey (or each fire-separated area / temporary building). The
            calculator works out the Class A extinguishers each one needs.
          </p>

          <div className="mt-5 space-y-4">
            {rows.map((row, i) => (
              <div key={row.id} className="rounded-xl border border-line bg-mist/60 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-[0.8rem] font-bold uppercase tracking-wide text-muted">
                    Area {i + 1}
                  </span>
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setRows((rs) => rs.filter((r) => r.id !== row.id))}
                      className="text-[0.8rem] font-semibold text-muted hover:text-risk-veryhigh"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_180px]">
                  <Field label="Storey / area name">
                    <RememberedTextInput
                      store="sites"
                      value={row.label}
                      onChange={(e) => update(row.id, { label: e.target.value })}
                      placeholder="e.g. First floor, Site cabin"
                    />
                  </Field>
                  <Field label="Floor area (m²)">
                    <NumberInput
                      min={0}
                      step={1}
                      value={row.area}
                      onChange={(e) => update(row.id, { area: e.target.value })}
                      placeholder="e.g. 300"
                    />
                  </Field>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setRows((rs) => [...rs, emptyRow()])}
              className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
            >
              + Add another floor / area
            </button>
            <Field label="" className="min-w-[200px] flex-1">
              <RememberedTextInput
                store="sites"
                value={site}
                onChange={(e) => setSite(e.target.value)}
                placeholder="Site name (for the printed schedule)"
              />
            </Field>
          </div>
          <div className="mt-4">
            <RememberedNamesNote stores={["sites"]} />
          </div>
        </div>

        <div className="card shadow-soft p-6 print-hide">
          <h2 className="text-xl">Special risks present</h2>
          <p className="mt-1 text-sm text-muted">
            The floor-area maths sizes the Class A (wood, paper, textile) cover. Tick anything else on site
            so the schedule adds the right extinguisher type at the right place.
          </p>
          <div className="mt-4 space-y-2.5">
            {RISK_OPTIONS.map((o) => (
              <CheckboxRow key={o.key} checked={!!risks[o.key]} onChange={(v) => toggleRisk(o.key, v)}>
                {o.label}
              </CheckboxRow>
            ))}
          </div>
        </div>
      </div>

      {/* Result + printable schedule */}
      <div className="space-y-4">
        {computed ? (
          <>
            <div className="print-area card shadow-soft p-6" id="fire-extinguisher-schedule">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                Portable extinguisher provision schedule
              </div>
              <h2 className="mt-1 text-lg">
                {site ? `${site} — ` : ""}
                {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </h2>

              <div className="mt-4">
                <ResultBanner
                  level="medium"
                  title={`${computed.totalUnits} Class A extinguisher${computed.totalUnits === 1 ? "" : "s"} across ${computed.results.length} ${computed.results.length === 1 ? "area" : "areas"}`}
                >
                  Based on 13A-equivalent units (e.g. 9 L water or 6 L foam). Position them so no one is more
                  than {TRAVEL_DISTANCE} m from a Class A extinguisher, on escape routes and near exits.
                </ResultBanner>
              </div>

              <table className="mt-4 w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-[0.78rem] uppercase tracking-wide text-muted">
                    <th className="py-1.5 pr-2 font-semibold">Storey / area</th>
                    <th className="py-1.5 pr-2 text-right font-semibold">m²</th>
                    <th className="py-1.5 pr-2 text-right font-semibold">Req. A</th>
                    <th className="py-1.5 text-right font-semibold">13A units</th>
                  </tr>
                </thead>
                <tbody>
                  {computed.results.map((r) => (
                    <tr key={r.row.id} className="border-b border-line/60">
                      <td className="py-2 pr-2 text-ink">
                        {r.row.label || "Unnamed area"}
                        {r.minApplied && (
                          <span className="ml-1.5 rounded-full bg-mist px-1.5 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide text-muted">
                            {r.singleAllowed ? "min 1" : "min 2"}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-2 text-right">{r.area}</td>
                      <td className="py-2 pr-2 text-right">{Math.ceil(r.requiredRating)}A</td>
                      <td className="py-2 text-right font-semibold text-ink">{r.units}</td>
                    </tr>
                  ))}
                  <tr>
                    <td className="py-2 pr-2 font-semibold text-ink" colSpan={3}>
                      Total Class A extinguishers
                    </td>
                    <td className="py-2 text-right font-display text-lg font-bold text-ink">
                      {computed.totalUnits}
                    </td>
                  </tr>
                </tbody>
              </table>

              <p className="mt-2 text-[0.78rem] leading-relaxed text-muted">
                Class A rating needed per storey = 0.065 × floor area, with a minimum of 26A (two
                extinguishers) — a storey of 50 m² or less may be covered by one. A 13A extinguisher counts
                13 toward the total and covers roughly 200 m².
              </p>

              {/* Risk-specific type add-ons */}
              {computed.activeRisks.length > 0 ? (
                <div className="mt-4">
                  <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                    Add for the risks present
                  </div>
                  <ul className="mt-2 space-y-2">
                    {computed.activeRisks.map((o) => (
                      <li
                        key={o.key}
                        className="rounded-lg border border-line bg-mist/60 px-3 py-2 text-[0.85rem] leading-relaxed text-body"
                      >
                        {o.addOn}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="mt-4 rounded-lg bg-mist px-3 py-2 text-[0.82rem] leading-relaxed text-muted print-hide">
                  No special risks ticked — tick electrical, flammable liquids, hot works, cooking or gases on
                  the left to add the right extinguisher type for each.
                </p>
              )}

              <div className="mt-4 rounded-lg border border-risk-medium/40 bg-[#fdf6e3] px-3 py-2.5 text-[0.82rem] leading-relaxed text-[#8a6400]">
                <strong>Scope:</strong> this sizes portable extinguisher provision only. It is not a fire risk
                assessment and does not cover fixed systems, detection, alarm or means of escape. Temporary
                buildings and hot works on construction sites are governed by HSG168.
              </div>

              <DataReviewedNote date="6 June 2026">
                Provision logic follows BS 5306-8 Class A area cover and HSE construction fire guidance.
                Standards change — confirm against a competent person and your fire risk assessment.
              </DataReviewedNote>

              <PrintFooter toolName="fire extinguisher calculator" />
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <CopyButton tool="fire-extinguisher-calculator" buildText={buildCopyText} label="Copy schedule" />
              <PrintButton tool="fire-extinguisher-calculator" label="Print / save schedule as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Enter a floor area to see how many Class A extinguishers each storey needs and which extra types
            the risks on site call for.
          </div>
        )}
      </div>
    </div>
  );
}
